
import { Component, effect, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {MatCheckboxModule} from '@angular/material/checkbox';
import { AgentBuilderService } from '../services/agent-builder.service';
import { urlOrDefaultValidator, jsonValidator } from '../../../../../factory/custom-validators';

@Component({
  selector: 'agent-api-settings',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule
],
  templateUrl: './agent-api-settings.component.html',
  styleUrl: './agent-api-settings.component.scss'
})
export class AgentApiSettingsComponent {
  agentForm!: FormGroup;

  #formBuilder = inject(FormBuilder);
  #agentBuilderService = inject(AgentBuilderService);

  constructor() {
    effect(() => {
      if (this.#agentBuilderService.agent()) {
        this.agentForm = this.#formBuilder.group({
          apiUrl: new FormControl(this.#agentBuilderService.agent().details.apiUrl, [urlOrDefaultValidator]),
          usesGet: new FormControl(this.#agentBuilderService.agent().details.usesGet),
          apiHeaders: new FormControl(this.#agentBuilderService.agent().details.apiHeaders, [jsonValidator]),
          apiPayload: new FormControl(this.#agentBuilderService.agent().details.apiPayload, [jsonValidator]),
          sendUserInfo: new FormControl(this.#agentBuilderService.agent().details.sendUserInfo),
          sendSessionId: new FormControl(this.#agentBuilderService.agent().details.sendSessionId),
          sendMessageId: new FormControl(this.#agentBuilderService.agent().details.sendMessageId),
          sendUserPrompt: new FormControl(this.#agentBuilderService.agent().details.sendUserPrompt),
          requestManipulationCode: new FormControl(this.#agentBuilderService.agent().details.requestManipulationCode),
          responseManipulationCode: new FormControl(this.#agentBuilderService.agent().details.responseManipulationCode),
        });
      }
    });
  }

  /**
   * Marks all controls in the agent form as touched.
   * This is useful for triggering validation messages on all fields,
   * typically when the user attempts to submit the form.
   *
   * This is called programmatically in the validateAllChildForms method in the AgentBuilderComponent.
   */
  markFormAsTouched(): void {
    this.agentForm.markAllAsTouched();
  }

  /**
   * Validates the form and returns whether it's valid or not.
   * Used by the parent component to determine if this step is complete.
   * @returns A boolean indicating whether the form is valid.
   */
  isValid(): boolean {
    if (!this.agentForm) {
      return false;
    }

    this.agentForm.markAllAsTouched();
    
    // Check if API is enabled and if apiUrl is required
    const usesApi = this.#agentBuilderService.agent().details.usesApi;
    if (usesApi) {
      if (this.agentForm.get('apiUrl')?.value === '') {
        this.agentForm.get('apiUrl')?.setErrors({ required: true });
        return false;
      }
    }
    
    return this.agentForm.valid;
  }
}
